import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames'
import CloseLineIcon from 'remixicon-react/CloseLineIcon'
import debounce from 'lodash/debounce'
import cloneDeep from 'lodash/cloneDeep'
import Form from 'antd/lib/form'
import Avatar from '@UI/DataList/components/Avatar'
import Select from '@UI/antd/Select/Select'
import { listSize } from 'config'
import { rootActions } from '@store/slices'
import { RootState } from '@store/store'
import { getValueFromKey } from '@features/General'
import useStopPropagarionClickPopup from '@hooks/processor/useStopPropagarionClickPopup'
import { isValidMongoId } from '@utils/helpers/commonHelpers'
// import CompanyProfileImg from '@assets/images/company-profile.svg'
import styles from './index.scss'

const Option = Select.Option

interface IProps {
    module?: string
    listName?: string
    label?: string
    name: string
    projections?: string[]
    valueOption?: string
    displayOption: string[] | string
    sort?: string
    query?: any
    searchKeys?: string[]
    allowClear?: boolean
    onSelect?: any
    onChange?: any
    required?: boolean
    requiredMsg?: string
    rules?: any
    disabledOptions?: string[]
    options?: any
    disabled?: boolean
    multiple?: boolean
    hasCustomOptions?: boolean
    lazy?: boolean
    help?: string
    editValue?: string | string[]
    editRecord?: any
    suffixOption?: string
    modalForm?: boolean
    hideSelectedLabels?: boolean
    separatingSelectedLabels?: boolean
    containerId?: string
    maxTagCount?: number | string
    fieldKey?: string
    extraParams?: any
    logoField?: string
    fallbackImage?: any
    popupMatchSelectWidth?: boolean
    theme?: 'round-theme' | ''
    getLabel?: (option: any) => React.ReactNode
    logoFallbackTextFields?: string[]
    selectSearchOptionByClick?: boolean
}

const DynamicSelectField = (props: IProps) => {
    const {
        module = '',
        listName = 'list',
        label = '',
        name,
        projections = [],
        valueOption = '_id',
        displayOption,
        sort = '-createdAt',
        query = {},
        searchKeys = [],
        allowClear = true,
        onSelect = undefined,
        onChange = undefined,
        required = false,
        requiredMsg = '',
        rules = [],
        disabledOptions = [],
        options = [],
        disabled = false,
        multiple = false,
        hasCustomOptions = false,
        lazy = true,
        help = '',
        editValue = '',
        editRecord = null,
        suffixOption = '',
        modalForm = false,
        hideSelectedLabels = false,
        maxTagCount = '',
        containerId = '',
        fieldKey = '',
        separatingSelectedLabels = false,
        extraParams = {},
        logoField = '',
        // fallbackImage = CompanyProfileImg,
        popupMatchSelectWidth = true,
        theme = '',
        getLabel,
        logoFallbackTextFields = [],
        selectSearchOptionByClick = false
    } = props
    const dispatch = useDispatch()
    const { data: i18n } = useSelector((state: RootState) => state.localization.locals)
    const id = 'dynamic-select-' + fieldKey + name
    useStopPropagarionClickPopup(id)

    const [state, setState] = useState<any>({
        list: options.slice(0, listSize),
        editRecord: {},
        selectedData: [],
        isLoading: false,
        searchValue: '',
        searchIsSelected: false,
        skip: 0,
        total: options.length
    })

    const [open, setOpen] = useState<boolean>(false)

    const requestList = value => {
        setState(state => ({
            ...state,
            searchValue: value,
            skip: 0,
            list: [],
            isLoading: true
        }))
        handleGetData('search', 0, value)
    }

    const debouncedSearch = useCallback(debounce(requestList, 500), [JSON.stringify(query)])

    const onSearch = value => {
        debouncedSearch(value)
    }

    useEffect(() => {
        if (editValue) {
            handleGetData('initial', 0, '', editValue)
        }
    }, [editValue?.length])

    const handleGetData = useCallback(
        (mode, skip = 0, searchValue = '', editValue = '') => {
            if (options.length) {
                let newData = []
                if (searchValue) {
                    newData = options.filter(item => item.label.toLowerCase().includes(searchValue.toLowerCase()))
                } else {
                    const clonedOptions = options.slice(0)
                    newData = clonedOptions.splice(skip, listSize)
                    if (editValue.length) {
                        if (typeof editValue === 'string' && !newData.find(item => item.value === editValue)) {
                            newData.push(options.find(item => item.value === editValue))
                        } else if (Array.isArray(editValue)) {
                            editValue.forEach(value => {
                                if (!newData.find(item => item?.value === value)) {
                                    newData.push(options.find(item => item.value === value))
                                }
                            })
                        }
                    }
                }
                setState(state => ({
                    ...state,
                    list: state.skip > 0 ? [...state.list, ...newData] : newData,
                    skip: mode === 'initial' ? listSize : state.skip + listSize,
                    isLoading: false,
                    total: searchValue ? newData.length : options.length
                }))
            } else if (module) {
                const params: any = {
                    pagination: {
                        limit: listSize,
                        hasTotal: true,
                        skip
                    },
                    ...extraParams,
                    query: { ...query },
                    sagaCB: {
                        onSuccess: result => {
                            const resultsData = result?.data || result || []
                            if (editValue) {
                                const data = typeof editValue === 'string' ? resultsData[0] : resultsData
                                setState(state => ({
                                    ...state,
                                    editRecord: resultsData.length ? data : {},
                                    selectedData: resultsData.length ? data : []
                                }))
                            } else {
                                setState(state => ({
                                    ...state,
                                    list: state.skip > 0 ? [...state.list, ...resultsData] : resultsData,
                                    isLoading: false,
                                    skip: mode === 'initial' ? listSize : state.skip + listSize,
                                    total: result?.total || 0
                                }))
                            }
                        },
                        onError: () => {
                            setState(state => ({
                                ...state,
                                isLoading: false
                            }))
                        }
                    }
                }

                if (!editValue && searchKeys?.length && searchValue) {
                    params.query = {
                        ...params.query,
                        ...(searchKeys.length > 1
                            ? { $or: searchKeys.map(item => ({ [item]: { $regex: `(?i)${searchValue.trim()}` } })) }
                            : { [searchKeys[0]]: { $regex: `(?i)${searchValue.trim()}` } })
                    }
                }

                if (editValue) {
                    params.query = {
                        _id: typeof editValue === 'string' ? editValue : { $in: editValue }
                    }
                }

                if (projections?.length) {
                    params.necessaryProjectionArray = projections
                }

                if (sort?.length) {
                    params.pagination.sort = sort
                }
                dispatch(rootActions[module][listName].onRequest(params))
            }
        },
        [JSON.stringify(query), module, state.searchValue, options.length]
    )

    const onScroll = event => {
        const target = event.target
        if (
            !state.isLoading &&
            target.scrollTop + target.offsetHeight === target.scrollHeight &&
            state.list.length < state.total
        ) {
            target.scrollTo(0, target.scrollHeight)
            setState(state => ({
                ...state,
                isLoading: true
            }))
            handleGetData('infinite', state.skip, state.searchValue)
        }
    }

    const refetch = useRef(true)
    const refetchDeps = [JSON.stringify(query), handleGetData]

    useEffect(() => {
        refetch.current = true
    }, refetchDeps)

    useEffect(() => {
        if (!open) {
            setState(state => ({
                ...state,
                searchValue: ''
            }))
        }
        if ((lazy && !open) || !refetch.current) return
        refetch.current = false
        setState(state => ({
            ...state,
            list: [],
            isLoading: true,
            skip: 0,
            total: 0
        }))
        handleGetData('initial', 0, state.searchValue)
    }, [...refetchDeps, open, lazy])

    const multipleProp: any = multiple ? { mode: 'multiple' } : {}
    const allOptions = useMemo(() => {
        const result = cloneDeep(state.list)
        if (editValue) {
            if (state.editRecord?._id && !result.find(item => item._id === state.editRecord?._id))
                result.push(state.editRecord)
            else if (Array.isArray(state.editRecord)) result.push(...state.editRecord)
        }
        if (editRecord?._id) {
            if (editRecord?._id && !result.find(item => item._id === editRecord?._id)) {
                result.push(editRecord)
            }
        } else if (Array.isArray(editRecord)) {
            editRecord.forEach(item => {
                if (item?._id && !result.find(x => x._id === item?._id)) {
                    result.push(item)
                }
            })
        }
        return result
    }, [state.list, editValue, editRecord])

    const getSuffixText = option => {
        const modifier = suffixOption.split('.')
        let i = 0
        let value = option[modifier[i]]
        while (modifier.length > i + 1) {
            i++
            value = value[modifier[i]]
        }
        return value || ''
    }
    const showCustomOption = useMemo(
        () => hasCustomOptions && state.searchValue.trim(),
        [hasCustomOptions, state.searchValue]
    )

    useEffect(() => {
        setState(state => ({ ...state, searchIsSelected: false }))
    }, [state.searchValue])

    return (
        <div id={id} className={styles[theme]}>
            <Form.Item
                noStyle
                shouldUpdate={(prev, next) =>
                    multiple ? JSON.stringify(prev[name]) !== JSON.stringify(next[name]) : prev[name] !== next[name]
                }
            >
                {({ getFieldValue, setFieldValue }) => {
                    if (showCustomOption && !state.searchIsSelected && !selectSearchOptionByClick) {
                        setState(state => ({ ...state, searchIsSelected: true }))
                        const modifier = getFieldValue(name) || []
                        const newValue = multiple ? [...modifier] : state.searchValue
                        if (multiple && !modifier.includes(state.searchValue)) {
                            newValue.push(state.searchValue)
                        }
                        setFieldValue(name, newValue)
                        if (typeof onSelect === 'function') {
                            onSelect(state.searchValue)
                        }
                    }
                    const modifier = getFieldValue(name)
                    const value = multiple && typeof modifier !== 'string' ? modifier?.[0] : modifier
                    return (
                        <>
                            <Form.Item
                                style={{ marginBottom: help?.trim() ? 4 : 'auto' }}
                                name={name}
                                rules={[
                                    {
                                        required: required,
                                        message:
                                            requiredMsg || `${i18n?.General?.Please_choose || 'Please choose'} ${label}`
                                    },
                                    ...rules
                                ]}
                                className={classNames(styles['dynamic-select-field'], {
                                    [styles['multiple-select']]: multiple
                                })}
                            >
                                <Select
                                    onSearch={searchKeys.length > 0 ? onSearch : undefined}
                                    filterOption={false}
                                    style={{ width: '100%' }}
                                    showSearch={searchKeys.length > 0 || hasCustomOptions}
                                    loading={state.isLoading}
                                    label={label ? label + (required ? ' *' : '') : ''}
                                    allowClear={allowClear}
                                    onPopupScroll={onScroll}
                                    onSelect={onSelect}
                                    popupMatchSelectWidth={popupMatchSelectWidth}
                                    maxTagCount={maxTagCount}
                                    getPopupContainer={() => {
                                        if (modalForm) {
                                            return document.body
                                        } else if (containerId) {
                                            return document.getElementById(containerId)
                                        }
                                        return document.getElementById(id)
                                    }}
                                    onChange={(value, option) => {
                                        if (separatingSelectedLabels) {
                                            setState(state => ({ ...state, selectedData: option.map(x => x.info) }))
                                        }
                                        if (typeof onChange === 'function') onChange(value, option)
                                    }}
                                    onDropdownVisibleChange={setOpen}
                                    disabled={disabled}
                                    className={classNames({
                                        [styles['value-not-exist-in-options']]:
                                            hideSelectedLabels ||
                                            (!multiple &&
                                                isValidMongoId(value) &&
                                                !allOptions.map(item => item?.[valueOption]).includes(value)),
                                        [styles['separating-selected']]: separatingSelectedLabels
                                    })}
                                    popupClassName={styles['dynamic-select-options']}
                                    {...multipleProp}
                                >
                                    {allOptions?.map((option, index) => (
                                        <Option
                                            info={option}
                                            key={`${option?.key || option?.[valueOption]}-${index}`}
                                            value={option?.[valueOption]}
                                            disabled={disabledOptions.includes(option?.[valueOption])}
                                        >
                                            <span className={styles['option-container']}>
                                                {logoField && (
                                                    <Avatar
                                                        img={option?.[logoField]}
                                                        size={30}
                                                        fallbackText={logoFallbackTextFields
                                                            .map(key => option?.[key])
                                                            .join(' ')}
                                                    />
                                                )}
                                                {typeof getLabel === 'function'
                                                    ? getLabel(option)
                                                    : getValueFromKey(option, displayOption)}
                                            </span>
                                            {suffixOption && <span>{getSuffixText(option)}</span>}
                                        </Option>
                                    ))}
                                    {allOptions?.length > 0 && (
                                        <Option
                                            disabled
                                            value={''}
                                            label={undefined}
                                            className={styles['fix-scroller-option']}
                                        >
                                            {''}
                                        </Option>
                                    )}

                                    {showCustomOption && (
                                        <Option key={state.searchValue} value={state.searchValue}>
                                            {state.searchValue}
                                        </Option>
                                    )}
                                </Select>
                            </Form.Item>
                            {separatingSelectedLabels && (
                                <div className={styles.labels}>
                                    {state.selectedData.map(item => (
                                        <span key={item[valueOption]}>
                                            {getValueFromKey(item, displayOption)}
                                            <CloseLineIcon
                                                size={12}
                                                onClick={() => {
                                                    const newValue = getFieldValue(name).filter(
                                                        x => x !== item[valueOption]
                                                    )
                                                    setFieldValue(name, newValue)
                                                    setState(state => {
                                                        return {
                                                            ...state,
                                                            selectedData: state.selectedData.filter(
                                                                x => x[valueOption] !== item[valueOption]
                                                            )
                                                        }
                                                    })
                                                }}
                                            />
                                        </span>
                                    ))}
                                </div>
                            )}
                        </>
                    )
                }}
            </Form.Item>
            {help.trim() && <div className={styles['help-text']}>{help.trim()}</div>}
        </div>
    )
}

export default DynamicSelectField
